import { currencyFormat } from "../util";
import { Group } from "./group.model";
import { FlockScenario } from "./sheep-model-parameters.model";
import { ValueItemView } from "./value-item-view.model";

export const LambBirthWeightKg = 4;

export function isFlockValueModel(o: unknown): o is FlockValueModel {
  return "flockValueAtTupping" in (o as FlockValueModel);
}

export interface FlockValueModel extends Required<Group> {
  flockValueAtTupping: number;
  eweMortality: number;
  eweMortalityPreScanning: number;
  ewesAliveAtScan: number;
  lambsAliveAtScan: number;
  eweMortalityPreLambing: number;
  lambLossDueToEweDeath: number;
  ewesAliveAtLambing: number;
  lambsAliveAtLambing: number;
  abortedEwes: number;
  abortedLambs: number;
  lambsBornFullTerm: number;
  eweMortalityLambingTo8Weeks: number;
  ewesAliveAt8Weeks: number;
  lambMortalityTo8Weeks: number;
  lambsAliveAt8Weeks: number;
  lambWeightAt8WeeksKg: number;
  totalLambWeightAt8WeeksKg: number;
  eweMortality8WeeksToWeaning: number;
  ewesAliveAtWeaning: number;
  lambMortality8WeeksToWeaning: number;
  lambsAliveAtWeaning: number;
  lambWeightIncrease8WeeksToWeaningKg: number;
  lambWeightAtWeaningKg: number;
  totalLambWeightAtWeaningKg: number;
  lambValueAtWeaning: number;
  breedingFlockValue: number;
  juvenileCropValue: number;
  flockValue: number;
  valuePerHead: number;
  valueGain: number;
  valueGainPerHead: number;
  totalLambLosses: number;
}

// TODO: consider refactoring or renaming this; it's not a model
export const getFlockValue = (
  flock: Required<Group>,
  scenario: FlockScenario
): FlockValueModel => {
  const flockValueAtTupping =
    flock.breedingMotherInitialHeadCount * flock.breedingMotherValuePerHead;

  // B14 =$B9*B13
  const eweMortality =
    flock.breedingMotherInitialHeadCount * scenario.eweMortalityFraction;

  // B17 = B14 / 4
  const eweMortalityPreScanning = eweMortality / 4;

  // B18 = $B9-B17
  const ewesAliveAtScan =
    flock.breedingMotherInitialHeadCount - eweMortalityPreScanning;

  // B19 = B18 * B16
  const lambsAliveAtScan = ewesAliveAtScan * scenario.scanFraction;

  // B22 = B14 / 4
  const eweMortalityPreLambing = eweMortality / 4;

  // B23 = B22 * B16
  const lambLossDueToEweDeath = eweMortalityPreLambing * scenario.scanFraction;

  // B24 = B18 - B22
  const ewesAliveAtLambing = ewesAliveAtScan - eweMortalityPreLambing;

  const lambsAliveAtLambing = ewesAliveAtLambing * scenario.scanFraction;

  // B25 = B21 * B24
  const abortedEwes = scenario.abortionFraction * ewesAliveAtLambing;

  // B26 = B25 * B16
  const abortedLambs = scenario.abortionFraction * lambsAliveAtLambing;

  // B27 = B19 - B23 - B26
  const lambsBornFullTerm =
    lambsAliveAtScan - lambLossDueToEweDeath - abortedLambs;

  // B31 = B14 / 4
  const eweMortalityLambingTo8Weeks = eweMortality / 4;

  // B32 =B24-B31
  const ewesAliveAt8Weeks = ewesAliveAtLambing - eweMortalityLambingTo8Weeks;

  // B33 =B27*B29
  const lambMortalityTo8Weeks =
    lambsBornFullTerm * scenario.lambMortalityBirthTo8Weeks;

  // B34 = B27 - B33
  const lambsAliveAt8Weeks = lambsBornFullTerm - lambMortalityTo8Weeks;

  // B35 = 4+(56*B30)/1000
  const lambWeightAt8WeeksKg =
    LambBirthWeightKg + (8 * 7 * scenario.growthRateTo8WeeksGramsPerDay) / 1000;

  // B36 = B35 * B34
  const totalLambWeightAt8WeeksKg = lambWeightAt8WeeksKg * lambsAliveAt8Weeks;

  // B40 = B14 / 4
  const eweMortality8WeeksToWeaning = eweMortality / 4;

  // B41 = B32 - B40
  const ewesAliveAtWeaning = ewesAliveAt8Weeks - eweMortality8WeeksToWeaning;

  // B42 = B34 * B38
  const lambMortality8WeeksToWeaning =
    lambsAliveAt8Weeks * scenario.lambMortality8To12Weeks;

  // B43 = B34 - B42
  const lambsAliveAtWeaning = lambsAliveAt8Weeks - lambMortality8WeeksToWeaning;

  // B44 = (28*B39)/1000
  const lambWeightIncrease8WeeksToWeaningKg =
    (4 * 7 * scenario.growthRate8To12WeeksGramsPerDay) / 1000;

  // B45 = B44+B35
  const lambWeightAtWeaningKg =
    lambWeightIncrease8WeeksToWeaningKg + lambWeightAt8WeeksKg;

  // B46 = B45 * B43
  const totalLambWeightAtWeaningKg =
    lambWeightAtWeaningKg * lambsAliveAtWeaning;

  // B47 = B46 * $B7
  const lambValueAtWeaning =
    totalLambWeightAtWeaningKg * flock.liveWeightJuvenileValuePerKg;

  // B48 = B41 * $B8
  const eweValueAtWeaning =
    ewesAliveAtWeaning * flock.breedingMotherValuePerHead;

  // B49 = B47 + B48
  const flockValue = lambValueAtWeaning + eweValueAtWeaning;

  // B50 = B49 / $B9
  const valuePerHead = flockValue / flock.breedingMotherInitialHeadCount;

  const valueGain = flockValue - flockValueAtTupping;

  const valueGainPerHead = valueGain / flock.breedingMotherInitialHeadCount;

  const totalLambLosses =
    lambLossDueToEweDeath +
    abortedLambs +
    lambMortalityTo8Weeks +
    lambMortality8WeeksToWeaning;

  return {
    flockValueAtTupping,
    eweMortality,
    eweMortalityPreScanning,
    ewesAliveAtScan,
    lambsAliveAtScan,
    eweMortalityPreLambing,
    lambLossDueToEweDeath,
    ewesAliveAtLambing,
    lambsAliveAtLambing,
    abortedEwes,
    abortedLambs,
    lambsBornFullTerm,
    eweMortalityLambingTo8Weeks,
    ewesAliveAt8Weeks,
    lambMortalityTo8Weeks,
    lambsAliveAt8Weeks,
    lambWeightAt8WeeksKg,
    totalLambWeightAt8WeeksKg,
    eweMortality8WeeksToWeaning,
    ewesAliveAtWeaning,
    lambMortality8WeeksToWeaning,
    lambsAliveAtWeaning,
    lambWeightIncrease8WeeksToWeaningKg,
    lambWeightAtWeaningKg,
    totalLambWeightAtWeaningKg,
    lambValueAtWeaning,
    breedingFlockValue: eweValueAtWeaning,
    juvenileCropValue: lambValueAtWeaning,
    flockValue,
    valuePerHead,
    valueGain,
    valueGainPerHead,
    totalLambLosses,
    ...flock,
  };
};

type FlockValueViewType = {
  [key in keyof FlockValueModel]?: ValueItemView;
};

export const FlockValueView: FlockValueViewType = {
  breedingMotherInitialHeadCount: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Ewes to the tup",
  },
  flockValueAtTupping: {
    includedInVerboseView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Flock value at tupping",
    toolTip: "The value of the breeding females at tupping",
  },
  ewesAliveAtScan: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Ewes alive at scan",
    toolTip:
      "Note that in this tool one quarter of the annual ewe mortality is assumed to occur before scan",
  },
  lambsAliveAtScan: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Lambs in utero at scan",
    toolTip: "The total number of lambs scanned",
  },
  lambsBornFullTerm: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Lambs born alive",
    toolTip: "Note that the ewe deaths and abortions were taken into account",
  },
  lambsAliveAt8Weeks: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Lambs alive at 8 weeks",
  },
  lambsAliveAtWeaning: {
    includedInVerboseView: true,
    format: ({ value }) => Math.round(value).toString(),
    description: "Lambs alive at weaning",
    toolTip: "Weaning is assumed to be 12 weeks",
  },
  lambWeightAtWeaningKg: {
    includedInVerboseView: true,
    format: ({ value }) => `${value.toFixed(1)} kg`,
    description: "Lamb weight at weaning",
    toolTip: `Assumed birth weight is ${LambBirthWeightKg} kg`,
  },
  breedingFlockValue: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value of breeding flock at weaning",
  },
  juvenileCropValue: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value of lambs at weaning",
  },
  flockValue: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Total flock value at weaning",
    emphasized: true,
    toolTip:
      "Total of Value of breeding flock at weaning and Value of lambs at weaning",
  },
  valueGain: {
    includedInVerboseView: true,
    includedInBriefView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Total flock value gain",
    toolTip:
      "The difference between the total flock value at tupping and the flock value at weaning",
    emphasized: true,
  },
  valueGainPerHead: {
    includedInVerboseView: true,
    format: ({ value }) => currencyFormat.format(value),
    description: "Value gain per ewe",
    toolTip:
      "Total flock value at weaning divided by the number of females to the tup",
  },
};
